/**
 * Parses an import-file Timestamp cell into a JS Date. Imported responses must carry the
 * file's real timestamp (analytics filters responses by date range), so we never fall back
 * to "now" — an unparseable value returns null and the caller decides how to handle it.
 *
 * Accepts:
 * - a `Date` (xlsx read with `cellDates: true`) → returned as-is;
 * - the Dutch export string `D-M-YYYY` or `D-M-YYYY, HH:mm:ss`.
 *
 * @param {Date|string|null|undefined} value - The raw Timestamp cell value.
 * @returns {Date|null} The parsed date, or null when blank/unparseable.
 */
const parseImportTimestamp = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

    const str = String(value ?? '').trim();
    if (!str) return null;

    const [datePart, timePart] = str.split(',').map((s) => s.trim());
    const dateMatch = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(datePart);
    if (!dateMatch) return null;

    const day = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const year = Number(dateMatch[3]);

    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (timePart) {
        const timeMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timePart);
        if (!timeMatch) return null;
        hours = Number(timeMatch[1]);
        minutes = Number(timeMatch[2]);
        seconds = Number(timeMatch[3] || 0);
    }

    const date = new Date(year, month - 1, day, hours, minutes, seconds);
    return Number.isNaN(date.getTime()) ? null : date;
};

export default parseImportTimestamp;
